// React import
import React, { useEffect, useState, Fragment, useCallback } from "react";
// Material import
import { styled } from "@mui/system";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
// custom component import
import CustomTooltip from "../../../utils/CustomTooltip";
import styles from "./prioritize.module.css";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SingleSelectWithInput from "Samarth Module/components/dropdown/SingleSelectWithInput";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "var(--primany-table-header-color)",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        outline: "1px solid #EAECF0",
        whiteSpace: "nowrap",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: "12px",
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        whiteSpace: "nowrap",
        // backgroundColor: value ? getCellBgColor(value, colname) : "#FFFFFF",
    },
}));

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

const StyledTableSubHeaderCell = styled(TableCell)({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#F5F5F5",
        color: "var(--black-color)",
        textAlign: "center",
        padding: "2px",
        fontSize: "12px",
        fontWeight: 600,
        outline: "1px solid #DBDBDB",
        borderBottom: "none",
        whiteSpace: "nowrap",
    },
});

const StyledLeftEmptyCell = {
    backgroundColor: "#FFFFFF !important",
    outline: "none !important",
    borderLeft: "1px solid #EAECF0 !important",
    borderBottom: "1px solid #EAECF0 !important"
};

const getCurrentWkBgColor = (wkc)=>{
    if("Current week n" === wkc?.label){
        return{backgroundColor:"#E0E0E0"}
    }
}

const getBGColor = (value, col, is_edit) => {
    if('Priority' === col){
        if('Prio 1' === value && is_edit){
            return {
                backgroundColor: "#FF9998",
                border: "1px solid #FF9998",
                borderRadius: '4px',
                padding: '0px 10px'
            };
        } 
        if('Prio 2' === value && is_edit){
            return {
                backgroundColor: "#FFEC9A",
                border: "1px solid #FFEC9A",
                borderRadius: '4px',
                padding: '0px 10px'
            };
        }
        if('Prio 3' === value && is_edit){
            return {
                backgroundColor: "#A3E1D2",
                border: "1px solid #A3E1D2",
                borderRadius: '4px',
                padding: '0px 10px'
            };
        }
    }else{
        return "#FFFFFF";
    }
}
// for fetching the current week number
function getWeekNumber(nxtwk, date = new Date()) {
    const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = tempDate.getUTCDay() || 7;

    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - day);

    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7);

    return weekNo + nxtwk;
}
const tableWeekColumns = [
    {
        "label": "Current week n",
        "DisplayLabel": `Wk ${getWeekNumber(0)}`,
        "visibility": true,
        "DisplayOrder": 1,
        "sortable": false
    },
    {
        "label": "Wk n+1",
        "DisplayLabel": `Wk ${getWeekNumber(1)}`,
        "visibility": true,
        "DisplayOrder": 2,
        "sortable": false
    },
    {
        "label": "Wk n+2",
        "DisplayLabel": `Wk ${getWeekNumber(2)}`,
        "visibility": true,
        "DisplayOrder": 3,
        "sortable": false
    },
    {
        "label": "Wk n+3",
        "DisplayLabel": `Wk ${getWeekNumber(3)}`,
        "visibility": true,
        "DisplayOrder": 4,
        "sortable": false
    },
    {
        "label": "Wk n+4",
        "DisplayLabel": `Wk ${getWeekNumber(4)}`,
        "visibility": true,
        "DisplayOrder": 5,
        "sortable": false
    }
];
const MatrixTable = ({tableColumns=[], subHeaderColumn=[], displayData=[], subTotal={}, sentCheckBoxValues, loading, handlePriorityChangeByDropdown, onSort, sortBy='', sortOrder='', fieldSection=''})=>{
    const [tableData, setTableData] = useState([]);
    const [selected, setSelected] = useState([]);
    // Select All functionality start
    // Toggle "Select All"
    const handleSelectAllClick = useCallback((event)=>{
        if (event.target.checked) {
            const allIds = tableData.map((row) => row.id);
            setSelected(allIds);
        } else {
            setSelected([]);
        }
    }, [tableData]);
    // Toggle a single checkbox
    const handleCheckBoxClick = useCallback((id) => {
        setSelected((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
    }, []);
    // Update indeterminate state of "Select All" checkbox
    const allSelected = selected.length === tableData.length;
    const isIndeterminate = selected.length > 0 && !allSelected;
    const isSelected = (id) => selected.includes(id);

    // Notify parent when selected rows change
    useEffect(() => {
        sentCheckBoxValues(selected);
    }, [selected, sentCheckBoxValues]);
    // Select All functionality end

    const handleSortSubHeader = useCallback((objectKey, key) => {
        let direction = 'asc';

        // Check current sort state from parent props
        if (
            fieldSection === objectKey &&
            sortBy === key &&
            sortOrder?.toLowerCase() === 'asc'
        ) {
            direction = 'desc';
        }

        // Call parent's onSort callback for server-side sorting
        if (onSort) {
            onSort(key, direction, objectKey);
        }
    }, [fieldSection, sortBy, sortOrder, onSort]);



    // sorting functionality
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        // Check current sort state from parent props
        if (sortBy === key && sortOrder?.toLowerCase() === 'asc') {
            direction = 'desc';
        }

        // Call parent's onSort callback for server-side sorting
        if (onSort) {
            onSort(key, direction);
        }
    }, [sortBy, sortOrder, onSort]);

    useEffect(()=>{
        setTableData(displayData);
    }, [displayData]);
    const widthOffset = {"Site cluster":150, "Site code":80, "Format":150, "Basepack code":120, "Basepack description":150, "CBU code":80,"CBU description":150, "All":80}
    const getStickyStyle = (colIndex, type) => {
        let left = 0;
        const disColumn = tableColumns?.filter(col => col.visibility);
        for (let i = 0; i < colIndex; i++) {
            const colLabel = disColumn[i]?.label;
            left += widthOffset[colLabel]; // fallback width
        }
        return {
            position: 'sticky',
            left: `${left}px`,
            backgroundColor: 'inherit',
            zIndex: 'head' === type ? 6 : 3
        };
        
    };
    const getStickyWidth = () => {
        const disColumn = tableColumns?.filter(col => col.visibility)?.length + 1;//1 for All column
        let left = disColumn > 4 ? 9: 7;
        return (left * disColumn) + 1;
    };
    
    return(
        <TableContainer className={styles.table} style={{height: "calc(100vh - 315px)"}}>
            <Table size="small" stickyHeader>
                {/* Main header row */}
                <TableHead>
                    {/* Dynamic column */}
                {tableColumns?.length > 0 ? (
                    <Fragment>
                        <TableRow>
                            {tableColumns?.filter(a => a.visibility === true)?.map((col, index) => {
                            return (
                                <StyledTableCell
                                    key={`mainW-${col.label}-${index}`}
                                    onClick={() => col.sortable && handleSort(col.label)}
                                    sx={{
                                        cursor: col.sortable ? "pointer" : "default",
                                        top: 0,
                                        ...getStickyStyle(index, 'head'),
                                        minWidth:widthOffset[col.label],
                                        maxWidth:widthOffset[col.label]
                                    }}
                                >
                                    <div className={styles.sortIcon}>
                                        {col.label}
                                        {col.sortable && sortBy === col.label && (
                                            sortOrder?.toLowerCase() === 'desc' 
                                                ? <ArrowDownwardIcon sx={{height:'14px', width:'14px', marginLeft:'4px'}}/> 
                                                : <ArrowUpwardIcon sx={{height:'14px', width:'14px', marginLeft:'4px'}}/>
                                        )}
                                    </div>
                                </StyledTableCell>
                            );
                            })}
                            {/* Check box column */}
                            <StyledTableCell
                                key={`All`}
                                sx={{
                                    cursor: "pointer",
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 6,
                                    // left:,
                                    ...getStickyStyle(tableColumns?.filter(a => a.visibility === true)?.length, 'head')
                                }}
                            >
                                <div className={styles.sortIcon}>
                                    <Checkbox
                                        indeterminate={isIndeterminate}
                                        checked={allSelected}
                                        onChange={handleSelectAllClick}
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 14,  },
                                            '&.Mui-checked': {color: '#fff'},
                                            '&.MuiCheckbox-indeterminate': {
                                                color: '#fff',
                                            },
                                            color: '#fff',
                                            padding:'0px'}}
                                    /><span>All</span>
                                </div>
                            </StyledTableCell>
                            {/* check box column end */}
                            {/* Statick Column */}
                            {tableWeekColumns?.filter(a => a.visibility === true)?.map((col, index) => {
                            // For Current week n, include all subHeaderColumns; for others, exclude Daily MSTN and Daily OI
                            const subHeaderCount = col.label === 'Current week n' 
                                ? subHeaderColumn?.filter(c => c?.visibility)?.length 
                                : subHeaderColumn?.filter(c => c?.visibility && c?.label !== 'Daily MSTN' && c?.label !== 'Daily OI')?.length;
                            return (
                                <StyledTableCell
                                    key={`mainW-${col.label}-${index}`}
                                    colSpan={2 + subHeaderCount}
                                    onClick={() => col.sortable && handleSort(col.label)}
                                    sx={{
                                        cursor: col.sortable ? "pointer" : "default",
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2
                                    }}
                                >
                                    <div className={styles.sortIcon}>
                                        {col.DisplayLabel}
                                        {col.sortable && sortBy === col.label && (
                                            sortOrder?.toLowerCase() === 'desc' 
                                                ? <ArrowDownwardIcon sx={{height:'14px', width:'14px', marginLeft:'4px'}}/> 
                                                : <ArrowUpwardIcon sx={{height:'14px', width:'14px', marginLeft:'4px'}}/>
                                        )}
                                    </div>
                                </StyledTableCell>
                            );
                            })}
                        </TableRow>
                        <TableRow>
                            {/* empty sub header cell */}
                            <StyledTableSubHeaderCell
                                sx={{
                                    ...StyledLeftEmptyCell,
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 9,
                                    backgroundColor:'#FFFFFF',
                                    left:0,
                                }}
                                colSpan={tableColumns?.filter(itm=> itm?.visibility)?.length + 1 }
                            ></StyledTableSubHeaderCell>
                            {/* Current week n */}
                            {subHeaderColumn?.find(c=>c?.label==='Daily MSTN' && c?.visibility) && (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        cursor:'pointer',
                                        position: 'sticky',
                                        top: '35px',
                                        zIndex: 4
                                    }}
                                    onClick={() =>handleSortSubHeader('Current week n', 'Daily MSTN')}
                                >
                                    <div className={styles.sortIcon}>
                                        Daily MSTN                                   
                                        {fieldSection === 'Current week n' && sortBy === 'Daily MSTN' && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )}
                            {subHeaderColumn?.find(c=>c?.label==='Daily OI' && c?.visibility) && (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        cursor:'pointer',
                                        position: 'sticky',
                                        top: '35px',
                                        zIndex: 4
                                    }}
                                    onClick={() =>handleSortSubHeader('Current week n', 'Daily OI')}
                                >
                                    <div className={styles.sortIcon}>
                                        Daily OI                                   
                                        {fieldSection === 'Current week n' && sortBy === 'Daily OI' && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    cursor:'pointer',
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Current week n', 'Week plan')}
                            >
                               
                                <div className={styles.sortIcon}>
                                    Week plan                                    
                                    {fieldSection === 'Current week n' && sortBy === 'Week plan' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        cursor:'pointer',
                                        position: 'sticky',
                                        top: '35px',
                                        zIndex: 4
                                    }}
                                    onClick={() =>handleSortSubHeader('Current week n', 'Weekly MSTN')}
                                >
                                    <div className={styles.sortIcon}>
                                        Weekly MSTN                                   
                                        {fieldSection === 'Current week n' && sortBy === 'Weekly MSTN' && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )
                            }
                            {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) && (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        cursor:'pointer',
                                        position: 'sticky',
                                        top: '35px',
                                        zIndex: 4
                                    }}
                                    onClick={() =>handleSortSubHeader('Current week n', 'Weekly OI')}
                                >
                                    <div className={styles.sortIcon}>
                                        Weekly OI                                   
                                        {fieldSection === 'Current week n' && sortBy === 'Weekly OI' && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Current week n', 'Priority')}
                            >
                                <div className={styles.sortIcon}>
                                    Priority                                  
                                    {fieldSection === 'Current week n' && sortBy === 'Priority' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {/* week n+1 */}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+1', 'Week plan')}
                            >
                                <div className={styles.sortIcon}>
                                    Week plan                                    
                                    {fieldSection === 'Wk n+1' && sortBy === 'Week plan' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {subHeaderColumn?.filter(a => a.visibility && a.label !== 'Daily MSTN' && a.label !== 'Daily OI')?.map((scol, sindex) => {
                            return (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        position: 'sticky',
                                        top: '35px', // Height of the main header
                                        zIndex: 4
                                    }}
                                    key={`shab1${sindex}`}
                                    onClick={() =>handleSortSubHeader('Wk n+1', scol?.label)}
                                >
                                    <div className={styles.sortIcon}>
                                        {scol?.label}                                   
                                        {fieldSection === 'Wk n+1' && sortBy === scol?.label && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )})}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+1', 'Priority')}
                            >
                                <div className={styles.sortIcon}>
                                    Priority                                  
                                    {fieldSection === 'Wk n+1' && sortBy === 'Priority' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {/* week n+2 */}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+2', 'Week plan')}
                            >
                                <div className={styles.sortIcon}>
                                    Week plan                                    
                                    {fieldSection === 'Wk n+2' && sortBy === 'Week plan' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {subHeaderColumn?.filter(a => a.visibility && a.label !== 'Daily MSTN' && a.label !== 'Daily OI')?.map((scol, sindex) => {
                            return (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        position: 'sticky',
                                        top: '35px', // Height of the main header
                                        zIndex: 4
                                    }}
                                    key={`shab2${sindex}`}
                                    onClick={() =>handleSortSubHeader('Wk n+2', scol?.label)}
                                >
                                    <div className={styles.sortIcon}>
                                        {scol?.label}                                   
                                        {fieldSection === 'Wk n+2' && sortBy === scol?.label && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )})}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+2', 'Priority')}
                            >
                                <div className={styles.sortIcon}>
                                    Priority                                  
                                    {fieldSection === 'Wk n+2' && sortBy === 'Priority' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {/* week n+3 */}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+3', 'Week plan')}
                            >
                                <div className={styles.sortIcon}>
                                    Week plan                                    
                                    {fieldSection === 'Wk n+3' && sortBy === 'Week plan' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {subHeaderColumn?.filter(a => a.visibility && a.label !== 'Daily MSTN' && a.label !== 'Daily OI')?.map((scol, sindex) => {
                            return (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        position: 'sticky',
                                        top: '35px', // Height of the main header
                                        zIndex: 4
                                    }}
                                    key={`shab3${sindex}`}
                                    onClick={() =>handleSortSubHeader('Wk n+3', scol?.label)}
                                >
                                    <div className={styles.sortIcon}>
                                        {scol?.label}                                   
                                        {fieldSection === 'Wk n+3' && sortBy === scol?.label && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )})}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+3', 'Priority')}
                            >
                                <div className={styles.sortIcon}>
                                    Priority                                  
                                    {fieldSection === 'Wk n+3' && sortBy === 'Priority' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {/* week n+4 */}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+4', 'Week plan')}
                            >
                                <div className={styles.sortIcon}>
                                    Week plan                                    
                                    {fieldSection === 'Wk n+4' && sortBy === 'Week plan' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                            {subHeaderColumn?.filter(a => a.visibility && a.label !== 'Daily MSTN' && a.label !== 'Daily OI')?.map((scol, sindex) => {
                            return (
                                <StyledTableSubHeaderCell 
                                    sx={{
                                        position: 'sticky',
                                        top: '35px', // Height of the main header
                                        zIndex: 4
                                    }}
                                    key={`shab4${sindex}`}
                                    onClick={() =>handleSortSubHeader('Wk n+4', scol?.label)}
                                >
                                    <div className={styles.sortIcon}>
                                        {scol?.label}                                   
                                        {fieldSection === 'Wk n+4' && sortBy === scol?.label && (
                                            sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                        )}
                                    </div>
                                </StyledTableSubHeaderCell>
                            )})}
                            <StyledTableSubHeaderCell 
                                sx={{
                                    position: 'sticky',
                                    top: '35px', // Height of the main header
                                    zIndex: 4
                                }}
                                onClick={() =>handleSortSubHeader('Wk n+4', 'Priority')}
                            >
                                <div className={styles.sortIcon}>
                                    Priority                                  
                                    {fieldSection === 'Wk n+4' && sortBy === 'Priority' && (
                                        sortOrder?.toLowerCase() === 'desc' ? <ArrowDownwardIcon sx={{height:'12px', width:'12px'}}/> : <ArrowUpwardIcon sx={{height:'12px', width:'12px'}}/>
                                    )}
                                </div>
                            </StyledTableSubHeaderCell>
                        </TableRow>
                    </Fragment>
                ):(
                    <TableRow>
                        <StyledTableCell
                            sx={{ backgroundColor: "var(--primany-table-header-color)" }}
                        >
                            No columns available...
                        </StyledTableCell>
                    </TableRow>
                )}
                </TableHead>
                <TableBody>
                    {loading ? 
                    (
                        <StyledTableRow>
                            <StyledTableCell colSpan={tableColumns?.filter(a => a.visibility === true)?.length + (subHeaderColumn?.filter(a => a.visibility === true)?.length * 5) + 11} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                                Loading...
                            </StyledTableCell>
                        </StyledTableRow>
                    ) :
                    (tableData.length > 0 && tableColumns?.length > 0) ?  (
                        <Fragment>
                            {/* Sub total rows */}
                            <TableRow 
                            // className={styles.stickyHeader}
                            >
                                <StyledTableCell 
                                    sx={{
                                        position:'sticky', 
                                        zIndex:9, 
                                        left:0,
                                        top: '63px',
                                        backgroundColor:'#FFFFFF'
                                    }}
                                    colSpan={tableColumns?.filter(itm=> itm?.visibility)?.length + 1 }
                                >
                                    <span className={styles.subTotal}>Sub Total</span>
                                </StyledTableCell>
                                {subHeaderColumn?.find(c=>c?.label==='Daily MSTN' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                {subHeaderColumn?.find(c=>c?.label==='Daily OI' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                <StyledTableCell className={styles.subtotcelGray}>{subTotal?.["Current week n"]}</StyledTableCell>
                                {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) &&<StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}                             
                                <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>
                                <StyledTableCell className={styles.subtotcel}>{subTotal?.["Wk n+1"]}</StyledTableCell>
                                {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) &&<StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                <StyledTableCell className={styles.subtotcel}>-</StyledTableCell>
                                <StyledTableCell className={styles.subtotcel}>{subTotal?.["Wk n+2"]}</StyledTableCell>
                                {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) &&<StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                <StyledTableCell className={styles.subtotcel}>-</StyledTableCell>
                                <StyledTableCell className={styles.subtotcel}>{subTotal?.["Wk n+3"]}</StyledTableCell>
                                {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) &&<StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                <StyledTableCell className={styles.subtotcel}>-</StyledTableCell>
                                <StyledTableCell className={styles.subtotcel}>{subTotal?.["Wk n+4"]}</StyledTableCell>
                                {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && <StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) &&<StyledTableCell className={styles.subtotcelGray}>-</StyledTableCell>}
                                <StyledTableCell className={styles.subtotcel}>-</StyledTableCell>
                            </TableRow>
                            {tableData.map((alert, index) => (
                                <StyledTableRow key={alert?.id}>
                                    {tableColumns?.filter(a => a.visibility === true)?.map((col, colIdx) =>
                                        ['Site cluster', 'Format', 'Basepack description','CBU description'].includes(col?.label) ? 
                                        <StyledTableCell 
                                            key={`c${index + colIdx}`} 
                                            className={styles.ellipsisText}
                                            sx={{...getStickyStyle(colIdx, 'row')}}
                                        >
                                            <CustomTooltip title={alert[col?.label]}>
                                                <span>{alert[col?.label]}</span>
                                            </CustomTooltip>
                                        </StyledTableCell> : 
                                        <StyledTableCell 
                                            key={`c${index + colIdx}`}
                                            sx={{...getStickyStyle(colIdx, 'row')}}
                                        >
                                            {alert[col?.label]}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell 
                                        value={alert?.id} 
                                        colname={`id`} 
                                        key={`id${alert?.id}`}
                                        sx={{
                                            position: 'sticky',
                                            zIndex: 3,
                                            // left:0,
                                            backgroundColor:'#FFFFFF',
                                            ...getStickyStyle(tableColumns?.filter(a => a.visibility === true)?.length, 'row'),
                                        }}
                                    >
                                        <Checkbox
                                            checked={isSelected(alert.id)}
                                            onChange={() => handleCheckBoxClick(alert.id)}
                                            sx={{ '& .MuiSvgIcon-root': { fontSize: 15,  },
                                                padding:'0px',
                                                bottom:'2px' }}
                                        />
                                    </StyledTableCell>
                                    {tableWeekColumns?.filter(w => w.visibility === true)?.map((wc, wcolIdx) =>
                                        <Fragment key={`frag-${index}-${wcolIdx}`}>
                                            {wc.label === 'Current week n' && subHeaderColumn?.find(c=>c?.label==='Daily MSTN' && c?.visibility) && <StyledTableCell key={`DM-${index}-${wcolIdx}`} style={getCurrentWkBgColor(wc)}>{alert[wc.label]['Daily MSTN']}</StyledTableCell>}
                                            {wc.label === 'Current week n' && subHeaderColumn?.find(c=>c?.label==='Daily OI' && c?.visibility) && <StyledTableCell key={`DO-${index}-${wcolIdx}`} style={getCurrentWkBgColor(wc)}>{alert[wc.label]['Daily OI']}</StyledTableCell>}
                                            <StyledTableCell key={`w-${index}-${wcolIdx}`} style={getCurrentWkBgColor(wc)}>{alert[wc.label]['Week plan']}</StyledTableCell>
                                            {subHeaderColumn?.find(c=>c?.label==='Weekly MSTN' && c?.visibility) && <StyledTableCell key={`M-${index}-${wcolIdx}`} style={getCurrentWkBgColor(wc)}>{alert[wc.label]['Weekly MSTN']}</StyledTableCell>}
                                            {subHeaderColumn?.find(c=>c?.label==='Weekly OI' && c?.visibility) &&<StyledTableCell key={`S-${index}-${wcolIdx}`} style={getCurrentWkBgColor(wc)}>{alert[wc.label]['Weekly OI']}</StyledTableCell>}
                                            <StyledTableCell key={`P-${index}-${wcolIdx}`} style={getCurrentWkBgColor(wc)}>
                                                {(["Wk n+1", "Wk n+2"].includes(wc['label']) && alert[wc.label]['Priority']) ?
                                                    <SingleSelectWithInput
                                                        key={`select-${index}-${wcolIdx}`}
                                                        title={'Priority'}
                                                        placeholder={'Priority'}
                                                        width={"80px"}
                                                        backgroundColor={getBGColor(alert[wc.label]['Priority'], 'Priority', alert[wc.label]['is_edit'])}
                                                        label={'Priority'}
                                                        value={alert[wc.label]['Priority']}
                                                        options={['Prio 1', 'Prio 2', 'Prio 3']}
                                                        onChangeHandler={(_, value) => {handlePriorityChangeByDropdown(value, alert, wc.label)}}
                                                    /> :
                                                    <p style={getBGColor(alert[wc.label]['Priority'], 'Priority', alert[wc.label]['is_edit'])}>{alert[wc.label]['Priority']}</p>
                                                }
                                            </StyledTableCell>
                                        </Fragment>
                                    )}
                                </StyledTableRow>
                            ))}
                        </Fragment>
                    ): (
                        <StyledTableRow>
                            <StyledTableCell colSpan={tableColumns?.filter(a => a.visibility === true)?.length + (subHeaderColumn?.filter(a => a.visibility === true)?.length * 5) + 11} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                                {loading ? `Loading...`:`Data not available...`}
                            </StyledTableCell>
                        </StyledTableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
export default MatrixTable;